import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { 
  Menu, 
  X, 
  Droplets, 
  Wrench, 
  Settings, 
  HeadphonesIcon,
  Building2,
  CheckCircle2,
  ArrowRight,
  Linkedin,
  Mail,
  Globe,
  MapPin,
  Users,
  Package,
  Send,
  ChevronRight
} from "lucide-react";
import siteConfig from "@/config/siteConfig";
import { LanguageProvider, useLanguage } from "@/i18n/LanguageContext";

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

// Language Selector Component
const LanguageSelector = () => {
  const { language, setLanguage } = useLanguage();
  
  return (
    <div className="flex items-center gap-1 text-sm font-medium" data-testid="language-selector">
      <button
        onClick={() => setLanguage('en')}
        className={`px-2 py-1 rounded transition-colors ${
          language === 'en' 
            ? 'text-cyan-600 font-bold'
            : 'text-slate-500 hover:text-slate-700'
        }`}
        data-testid="lang-en-btn"
      >
        EN
      </button>
      <span className="text-slate-300">|</span>
      <button
        onClick={() => setLanguage('it')}
        className={`px-2 py-1 rounded transition-colors ${
          language === 'it' 
            ? 'text-cyan-600 font-bold'
            : 'text-slate-500 hover:text-slate-700'
        }`}
        data-testid="lang-it-btn"
      >
        IT
      </button>
    </div>
  );
};

// Header/Navigation Component
const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { t } = useLanguage();

  const navLinks = [
    { name: t('nav.about'), href: "#about" },
    { name: t('nav.services'), href: "#services" },
    { name: t('nav.markets'), href: "#markets" },
    { name: t('nav.howWeWork'), href: "#process" },
    { name: t('nav.contact'), href: "#contact" },
  ];

  const scrollToSection = (href) => {
    setIsMobileMenuOpen(false);
    if (href.startsWith("#")) {
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  };

  return (
    <header
      data-testid="main-header"
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 navbar-glass"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between py-2">
          {/* Logo */}
          <Link to="/" className="flex items-center flex-shrink-0 h-[52px] md:h-[60px]" data-testid="logo-link">
            <img 
              src={siteConfig.images.logo} 
              alt="Claritech Pool Systems logo" 
              className="h-full w-auto block"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-6" data-testid="desktop-nav">
            {navLinks.map((link, idx) => (
              <button
                key={idx}
                onClick={() => scrollToSection(link.href)}
                className="text-sm font-medium text-slate-700 hover:text-cyan-600 transition-colors"
                data-testid={`nav-link-${idx}`}
              >
                {link.name}
              </button>
            ))}
            <LanguageSelector />
            <button
              onClick={() => scrollToSection("#contact")}
              className="bg-cyan-600 text-white px-5 py-2.5 rounded-sm font-medium hover:bg-cyan-700 transition-all shadow-sm hover:shadow-md"
              data-testid="nav-business-inquiries"
            >
              {t('nav.businessInquiries')}
            </button>
          </nav>

          {/* Mobile: Language + Menu */}
          <div className="flex lg:hidden items-center gap-3">
            <LanguageSelector />
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2 text-slate-700"
              data-testid="mobile-menu-toggle"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-white border-t border-slate-100 py-4" data-testid="mobile-menu">
            {navLinks.map((link, idx) => (
              <button
                key={idx}
                onClick={() => scrollToSection(link.href)}
                className="block w-full text-left px-4 py-3 text-slate-700 hover:text-cyan-600 hover:bg-slate-50 font-medium"
                data-testid={`mobile-nav-link-${idx}`}
              >
                {link.name}
              </button>
            ))}
            <div className="px-4 pt-4">
              <button
                onClick={() => scrollToSection("#contact")}
                className="w-full bg-cyan-600 text-white px-6 py-3 rounded-sm font-medium hover:bg-cyan-700 transition-all"
                data-testid="mobile-business-inquiries"
              >
                {t('nav.businessInquiries')}
              </button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

// Hero Section
const Hero = () => {
  const { t } = useLanguage();
  
  const scrollToContact = () => {
    document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      style={{
        backgroundImage: `url(${siteConfig.images.hero})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      {/* Dark overlay */}
      <div className="absolute inset-0 bg-slate-900/60" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left: Main content */}
          <div className="text-center lg:text-left">
            <p className="text-cyan-400 text-sm font-semibold tracking-widest uppercase mb-4 animate-fade-in">
              Italy • Canada • USA
            </p>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 font-heading animate-fade-in-up">
              {siteConfig.companyName}
            </h1>
            <p className="text-xl md:text-2xl text-slate-200 mb-6 animate-fade-in-up delay-100">
              {t('hero.tagline')}
            </p>
            <p className="text-lg text-slate-300 mb-8 max-w-xl mx-auto lg:mx-0 animate-fade-in-up delay-200">
              {t('hero.mainClaim')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start animate-fade-in-up delay-300">
              <button
                onClick={scrollToContact}
                className="bg-cyan-600 text-white px-8 py-4 rounded-sm font-semibold hover:bg-cyan-700 transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
                data-testid="hero-business-inquiries"
              >
                {t('nav.businessInquiries')}
                <ArrowRight size={18} />
              </button>
            </div>
          </div>

          {/* Right: Company Snapshot Card */}
          <div className="animate-fade-in-up delay-400">
            <div className="snapshot-card rounded-lg p-8 shadow-2xl border border-white/10">
              <h3 className="text-slate-900 font-bold text-lg mb-6 flex items-center gap-2">
                <Building2 className="text-cyan-600" size={22} />
                {t('snapshot.title')}
              </h3>
              <div className="space-y-5">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-cyan-50 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Globe className="text-cyan-600" size={20} />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-900">{t('snapshot.markets')}</p>
                    <p className="text-slate-600">{t('snapshot.marketsValue')}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-cyan-50 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Droplets className="text-cyan-600" size={20} />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-900">{t('snapshot.focus')}</p>
                    <p className="text-slate-600">{t('snapshot.focusValue')}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-cyan-50 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Users className="text-cyan-600" size={20} />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-900">{t('snapshot.clients')}</p>
                    <p className="text-slate-600">{t('snapshot.clientsValue')}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-cyan-50 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Package className="text-cyan-600" size={20} />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-900">{t('snapshot.model')}</p>
                    <p className="text-slate-600">{t('snapshot.modelValue')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-white/50 rounded-full flex items-start justify-center p-2">
          <div className="w-1.5 h-3 bg-white/70 rounded-full" />
        </div>
      </div>
    </section>
  );
};

// About Section
const About = () => {
  const { t } = useLanguage();
  
  return (
    <section id="about" data-testid="about-section" className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div>
            <p className="text-cyan-600 text-sm font-semibold tracking-widest uppercase mb-4">{t('about.label')}</p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-6 font-heading">
              {t('about.title')}
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed mb-6">
              {t('about.p1')}
            </p>
            <p className="text-lg text-slate-600 leading-relaxed">
              {t('about.p2')}
            </p>
          </div>
          <div className="relative">
            <img
              src={siteConfig.images.about}
              alt={t('about.imageAlt')}
              className="rounded-lg shadow-2xl w-full h-auto object-cover"
            />
            <div className="absolute -bottom-6 -left-6 bg-cyan-600 text-white p-6 rounded-lg shadow-xl hidden md:block">
              <p className="text-3xl font-bold">3</p>
              <p className="text-sm font-medium">{t('about.statsLabel')}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Services Section
const Services = () => {
  const { t } = useLanguage();
  
  const services = [
    {
      icon: Droplets,
      titleKey: 'services.chemicals.title',
      descKey: 'services.chemicals.description',
    },
    {
      icon: Wrench,
      titleKey: 'services.spareParts.title',
      descKey: 'services.spareParts.description',
    },
    {
      icon: Settings,
      titleKey: 'services.equipment.title',
      descKey: 'services.equipment.description',
    },
    {
      icon: HeadphonesIcon,
      titleKey: 'services.guidance.title',
      descKey: 'services.guidance.description',
    },
  ];

  return (
    <section id="services" data-testid="services-section" className="py-24 md:py-32 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <p className="text-cyan-600 text-sm font-semibold tracking-widest uppercase mb-4">{t('services.label')}</p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-6 font-heading">
            {t('services.title')}
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            {t('services.subtitle')}
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {services.map((service, index) => (
            <div
              key={index}
              className="service-card bg-white p-8 rounded-lg border border-slate-100 hover:border-cyan-200"
              data-testid={`service-card-${index}`}
            >
              <div className="w-14 h-14 bg-cyan-50 rounded-lg flex items-center justify-center mb-6">
                <service.icon className="text-cyan-600" size={28} />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">{t(service.titleKey)}</h3>
              <p className="text-slate-600 leading-relaxed">{t(service.descKey)}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Markets Section
const Markets = () => {
  const { t } = useLanguage();
  
  const markets = [
    { nameKey: "markets.italy", flag: "🇮🇹" },
    { nameKey: "markets.canada", flag: "🇨🇦" },
    { nameKey: "markets.usa", flag: "🇺🇸" },
  ];

  return (
    <section id="markets" data-testid="markets-section" className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <p className="text-cyan-600 text-sm font-semibold tracking-widest uppercase mb-4">{t('markets.label')}</p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-6 font-heading">
            {t('markets.title')}
          </h2>
        </div>

        <div className="grid md:grid-cols-3 gap-8 mb-12">
          {markets.map((market, index) => (
            <div
              key={index}
              className="market-card bg-slate-50 p-8 rounded-lg border border-slate-200 text-center hover:border-cyan-300 transition-all"
              data-testid={`market-card-${index}`}
            >
              <span className="text-5xl mb-4 block">{market.flag}</span>
              <h3 className="text-2xl font-bold text-slate-900">{t(market.nameKey)}</h3>
            </div>
          ))}
        </div>

        <p className="text-center text-slate-500 text-sm">
          {t('markets.note')}
        </p>
      </div>
    </section>
  );
};

// Process Section
const Process = () => {
  const { t } = useLanguage();
  
  const steps = [
    { number: "01", titleKey: "process.step1.title", descKey: "process.step1.description" },
    { number: "02", titleKey: "process.step2.title", descKey: "process.step2.description" },
    { number: "03", titleKey: "process.step3.title", descKey: "process.step3.description" },
    { number: "04", titleKey: "process.step4.title", descKey: "process.step4.description" },
  ];

  return (
    <section id="process" data-testid="process-section" className="py-24 md:py-32 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <p className="text-cyan-600 text-sm font-semibold tracking-widest uppercase mb-4">{t('process.label')}</p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-6 font-heading">
            {t('process.title')}
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            {t('process.subtitle')}
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {steps.map((step, index) => (
            <div key={index} className="relative" data-testid={`process-step-${index}`}>
              <div className="text-5xl font-bold text-cyan-100 mb-4">{step.number}</div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">{t(step.titleKey)}</h3>
              <p className="text-slate-600">{t(step.descKey)}</p>
              {index < steps.length - 1 && (
                <ChevronRight className="hidden lg:block absolute top-8 -right-4 text-slate-300" size={24} />
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Trust Section
const Trust = () => {
  const { t } = useLanguage();
  const reasons = t('trust.reasons') || [];

  return (
    <section data-testid="trust-section" className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div>
            <p className="text-cyan-600 text-sm font-semibold tracking-widest uppercase mb-4">{t('trust.label')}</p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-8 font-heading">
              {t('trust.title')}
            </h2>
            <div className="space-y-4">
              {Array.isArray(reasons) && reasons.map((reason, index) => (
                <div key={index} className="flex items-center gap-4" data-testid={`trust-reason-${index}`}>
                  <CheckCircle2 className="text-cyan-600 flex-shrink-0" size={24} />
                  <p className="text-lg text-slate-700">{reason}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="relative">
            <img
              src={siteConfig.images.trust}
              alt={t('trust.imageAlt')}
              className="rounded-lg shadow-2xl w-full h-auto object-cover"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

// Contact Section
const Contact = () => {
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState({
    name: "",
    company: "",
    email: "",
    country: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch(siteConfig.formspreeEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setSubmitStatus("success");
        setFormData({ name: "", company: "", email: "", country: "", message: "" });
      } else {
        setSubmitStatus("error");
      }
    } catch (error) {
      setSubmitStatus("error");
    }

    setIsSubmitting(false);
  };

  // Country options based on language
  const countryOptions = language === 'it' 
    ? [{ value: "Italy", label: "Italia" }, { value: "Canada", label: "Canada" }, { value: "USA", label: "USA" }, { value: "Other", label: t('contact.form.countryOther') }]
    : [{ value: "Italy", label: "Italy" }, { value: "Canada", label: "Canada" }, { value: "USA", label: "USA" }, { value: "Other", label: t('contact.form.countryOther') }];

  return (
    <section id="contact" data-testid="contact-section" className="py-24 md:py-32 bg-slate-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
          <div>
            <p className="text-cyan-400 text-sm font-semibold tracking-widest uppercase mb-4">{t('contact.label')}</p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 font-heading">
              {t('contact.title')}
            </h2>
            <p className="text-lg text-slate-300 mb-8">
              {t('contact.subtitle')}
            </p>
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-cyan-600/20 rounded-lg flex items-center justify-center">
                  <Mail className="text-cyan-400" size={24} />
                </div>
                <div>
                  <p className="text-sm text-slate-400">{t('contact.email')}</p>
                  <a href={`mailto:${siteConfig.email}`} className="text-white hover:text-cyan-400 transition-colors">
                    {siteConfig.email}
                  </a>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-cyan-600/20 rounded-lg flex items-center justify-center">
                  <MapPin className="text-cyan-400" size={24} />
                </div>
                <div>
                  <p className="text-sm text-slate-400">{t('contact.marketsLabel')}</p>
                  <p className="text-white">Italy • Canada • USA</p>
                </div>
              </div>
            </div>
            <p className="text-sm text-slate-500 mt-8 border-t border-slate-700 pt-6">
              {t('contact.note')}
            </p>
          </div>

          <div className="bg-white rounded-lg p-8 shadow-2xl" data-testid="contact-form-container">
            <form onSubmit={handleSubmit} data-testid="contact-form">
              <div className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-2">
                    {t('contact.form.fullName')} *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-sm focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 outline-none transition-all"
                    placeholder={t('contact.form.namePlaceholder')}
                    data-testid="contact-name-input"
                  />
                </div>
                <div>
                  <label htmlFor="company" className="block text-sm font-medium text-slate-700 mb-2">
                    {t('contact.form.company')}
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-sm focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 outline-none transition-all"
                    placeholder={t('contact.form.companyPlaceholder')}
                    data-testid="contact-company-input"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
                    {t('contact.form.emailLabel')} *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-sm focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 outline-none transition-all"
                    placeholder={t('contact.form.emailPlaceholder')}
                    data-testid="contact-email-input"
                  />
                </div>
                <div>
                  <label htmlFor="country" className="block text-sm font-medium text-slate-700 mb-2">
                    {t('contact.form.country')} *
                  </label>
                  <select
                    id="country"
                    name="country"
                    required
                    value={formData.country}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-sm focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 outline-none transition-all"
                    data-testid="contact-country-select"
                  >
                    <option value="">{t('contact.form.countryPlaceholder')}</option>
                    {countryOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-slate-700 mb-2">
                    {t('contact.form.message')} *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={4}
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-sm focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 outline-none transition-all resize-none"
                    placeholder={t('contact.form.messagePlaceholder')}
                    data-testid="contact-message-input"
                  />
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-cyan-600 text-white py-4 rounded-sm font-semibold hover:bg-cyan-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  data-testid="contact-submit-button"
                >
                  {isSubmitting ? t('contact.form.sending') : (
                    <>
                      {t('contact.form.submit')}
                      <Send size={18} />
                    </>
                  )}
                </button>
                {submitStatus === "success" && (
                  <p className="text-green-600 text-sm text-center" data-testid="form-success-message">
                    {t('contact.form.success')}
                  </p>
                )}
                {submitStatus === "error" && (
                  <p className="text-red-600 text-sm text-center" data-testid="form-error-message">
                    {t('contact.form.error')}
                  </p>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  const { t } = useLanguage();
  const currentYear = new Date().getFullYear();

  return (
    <footer data-testid="footer" className="footer-bar py-8 md:py-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <img 
              src={siteConfig.images.logo} 
              alt="Claritech Pool Systems logo" 
              className="h-8 w-auto object-contain"
            />
          </div>
          <nav className="flex items-center gap-6">
            <Link
              to="/privacy"
              className="text-sm text-slate-600 hover:text-cyan-600 transition-colors"
              data-testid="footer-privacy-link"
            >
              {t('footer.privacy')}
            </Link>
            <Link
              to="/terms"
              className="text-sm text-slate-600 hover:text-cyan-600 transition-colors"
              data-testid="footer-terms-link"
            >
              {t('footer.terms')}
            </Link>
          </nav>
        </div>
        <div className="border-t border-slate-200 mt-6 pt-6 text-center">
          <p className="text-sm text-slate-500">
            © {currentYear} {siteConfig.companyName}. {t('footer.copyright')}
          </p>
        </div>
      </div>
    </footer>
  );
};

// Home Page (all sections)
const Home = () => {
  return (
    <>
      <Hero />
      <About />
      <Services />
      <Markets />
      <Process />
      <Trust />
      <Contact />
    </>
  );
};

// Privacy Policy Page
const Privacy = () => {
  const { t } = useLanguage();
  const sections = t('privacy.sections') || [];

  return (
    <div className="min-h-screen bg-white pt-28">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h1 className="text-4xl font-bold text-slate-900 mb-8">{t('privacy.title')}</h1>
        <div className="prose prose-slate max-w-none">
          <p className="text-slate-600 mb-6">{t('privacy.lastUpdated')}</p>
          
          {Array.isArray(sections) && sections.map((section, idx) => (
            <div key={idx}>
              <h2 className="text-2xl font-semibold text-slate-900 mt-8 mb-4">{section.title}</h2>
              <p className="text-slate-600 mb-4">{section.content}</p>
              {section.list && (
                <ul className="list-disc pl-6 text-slate-600 mb-6">
                  {section.list.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              )}
              {idx === sections.length - 1 && (
                <span>
                  {" "}
                  <a href={`mailto:${siteConfig.email}`} className="text-cyan-600 hover:underline">
                    {siteConfig.email}
                  </a>
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Terms Page
const Terms = () => {
  const { t } = useLanguage();
  const sections = t('terms.sections') || [];

  return (
    <div className="min-h-screen bg-white pt-28">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h1 className="text-4xl font-bold text-slate-900 mb-8">{t('terms.title')}</h1>
        <div className="prose prose-slate max-w-none">
          <p className="text-slate-600 mb-6">{t('terms.lastUpdated')}</p>

          {Array.isArray(sections) && sections.map((section, idx) => (
            <div key={idx}>
              <h2 className="text-2xl font-semibold text-slate-900 mt-8 mb-4">{section.title}</h2>
              <p className="text-slate-600 mb-6">
                {section.content}
                {idx === sections.length - 1 && (
                  <>
                    {" "}
                    <a href={`mailto:${siteConfig.email}`} className="text-cyan-600 hover:underline">
                      {siteConfig.email}
                    </a>
                  </>
                )}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Main App Component
function App() {
  return (
    <LanguageProvider>
      <div className="App">
        <BrowserRouter>
          <ScrollToTop />
          <Header />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
          </Routes>
          <Footer />
        </BrowserRouter>
      </div>
    </LanguageProvider>
  );
}

export default App;
